import React, { useState, useEffect } from 'react';
import './Statistics.scss';
import { useStatisticsVM } from './useStatisticsVM';
import { MultiSelect } from 'components/MultiSelect/MultiSelect';
import { ContentBlock } from 'components/ContentBlock/ContentBlock';
import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import { Loader } from 'components/Loader/Loader';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { statusMap } from 'utils/enumFormatter';

// Couleurs pour les camemberts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

export const Statistics: React.FunctionComponent = () => {
    const { filter, setFilter, users, extractData, statisticsData, isLoading } = useStatisticsVM();
    
    return (
        <div className="statistics-page">
            {/* Section d'extraction des données */}
            <ContentBlock title="Extraction des prestations réalisées par les APPV">
                <div className="filter-container">
                    <div className="form">
                        <div className="form-line">
                            <div className="form-group" style={{ width: '50%' }}>
                                <label>Date de début</label>
                                <input
                                    type="date"
                                    name="startDate"
                                    placeholder="Date de début"
                                    onChange={(e) => setFilter({ ...filter, startDate: e.target.value })}
                                    value={filter.startDate}
                                />
                            </div>
                            <div className="form-group" style={{ width: '50%' }}>
                                <label>Date de fin</label>
                                <input
                                    type="date"
                                    name="endDate"
                                    placeholder="Date de fin"
                                    onChange={(e) => setFilter({ ...filter, endDate: e.target.value })}
                                    value={filter.endDate}
                                />
                            </div>
                            <div className="form-group">
                                <label>APPV(s)</label>
                                <MultiSelect
                                    name="appvs"
                                    placeholder="Selectionner des APPVs"
                                    values={filter.appvs}
                                    labelKey="label"
                                    valueKey="value"
                                    update={(e: { label: string; value: string }[]) => {
                                        setFilter({
                                            ...filter,
                                            appvs: e
                                        });
                                    }}
                                    options={users.map((user) => ({
                                        label: user.fullname,
                                        value: user.id
                                    }))}
                                    colors={[]}
                                />
                            </div>
                        </div>
                    </div>
                    <Button type="button" onClick={extractData} label="Extraire les données" />
                </div>
            </ContentBlock>

            {/* Section des statistiques */}
            <ContentBlock title="Tableau de bord statistiques">
                {isLoading ? (
                    <div className="loader-container">
                        <Loader />
                    </div>
                ) : statisticsData ? (
                    <div className="statistics-content">
                        {/* Indicateurs principaux */}
                        <div className="stats-cards">
                            <Card title="Dossiers actifs" className="stat-card">
                                <div className="stat-number primary">
                                    {statisticsData.nbPersonsIngoing}
                                </div>
                                <div className="stat-label">Nombre de dossiers actifs</div>
                            </Card>
                            
                            <Card title="Dossiers sortis" className="stat-card">
                                <div className="stat-number secondary">
                                    {statisticsData.nbPersonsOutgoing}
                                </div>
                                <div className="stat-label">Nombre de dossiers sortis</div>
                            </Card>
                            
                            <Card title="APPV" className="stat-card">
                                <div className="stat-number tertiary">
                                    {statisticsData.nbAssistants}
                                </div>
                                <div className="stat-label">Nombre d'APPV</div>
                            </Card>
                        </div>

                        {/* Graphiques */}
                        <div className="stats-charts">
                            <Card title="Répartition par statut" className="chart-card">
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <PieChart>
                                            <Pie
                                                data={statisticsData.nbPersonsPerStatus}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ status, count, percent }) => `${statusMap[status].label}: ${count} (${(percent * 100).toFixed(0)}%)`}
                                                outerRadius={120}
                                                fill="#8884d8"
                                                dataKey="count"
                                            >
                                                {statisticsData.nbPersonsPerStatus.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip 
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload[0]) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="custom-tooltip">
                                                                <p>{`${statusMap[data.status]?.label || data.status} : ${data.count}`}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            <Card title="Répartition par APPV" className="chart-card">
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <PieChart>
                                            <Pie
                                                data={statisticsData.nbPersonsPerResponsiblePerson}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ fullname, count, percent }) => `${fullname}: ${count} (${(percent * 100).toFixed(0)}%)`}
                                                outerRadius={120}
                                                fill="#8884d8"
                                                dataKey="count"
                                            >
                                                {statisticsData.nbPersonsPerResponsiblePerson.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip 
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload[0]) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="custom-tooltip">
                                                                <p>{`${data.fullname || data.status} : ${data.count} dossiers`}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>
                        </div>
                    </div>
                ) : (
                    <div className="no-data">
                        <p>Aucune donnée disponible</p>
                    </div>
                )}
            </ContentBlock>
        </div>
    );
};
